/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Badge,
	Box,
	Button,
	Container,
	Flex,
	Input,
	SimpleGrid,
	Spinner,
	Table,
	Tabs,
	TabList,
	Tab,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	useDisclosure,
	useToast,
} from '@chakra-ui/react';
import { gql, useMutation, useQuery } from '@apollo/client';
import BreadcrumbComponent from '../../../components/BreadcrumbComponent/BreadcrumbComponent';
import useStore from '../../../store';
import { isAdmin } from '../../../utils/role';
import c from '../../../constant';
import DigitizerDetailModal from './DetailModal';

const QUERY_STATS = gql`
	query getDigitizationQueueStats {
		getDigitizationQueueStats {
			pending
			processing
			done
			failed
		}
	}
`;

const QUERY_ITEMS = gql`
	query getDigitizationQueueItems($status: String, $limit: Float, $offset: Float) {
		getDigitizationQueueItems(status: $status, limit: $limit, offset: $offset) {
			totalRows
			items {
				id
				sourceType
				sourceId
				status
				error
				attempts
				fileCount
				failedFileCount
				sourceCreatedAt
				startedAt
				finishedAt
			}
		}
	}
`;

const RETRY_ITEM = gql`
	mutation retryDigitizationQueueItem($id: String!) {
		retryDigitizationQueueItem(id: $id)
	}
`;

const QUERY_CLEANUP_BUDGET = gql`
	query getAiCleanupBudget {
		getAiCleanupBudget {
			remainingInputTokens
			remainingOutputTokens
			updatedAt
		}
	}
`;

const QUERY_CLEANUP_STATS = gql`
	query getDigitizationCleanupStats {
		getDigitizationCleanupStats {
			awaitingCleanup
			cleaned
		}
	}
`;

const ADD_CLEANUP_TOKENS = gql`
	mutation addAiCleanupTokenBudget($inputTokens: Float!, $outputTokens: Float!) {
		addAiCleanupTokenBudget(inputTokens: $inputTokens, outputTokens: $outputTokens) {
			remainingInputTokens
			remainingOutputTokens
		}
	}
`;

const STATUS_TABS = ['pending', 'processing', 'done', 'failed'];
const PAGE_SIZE = 25;

const STATUS_COLOR = {
	pending: 'gray',
	processing: 'blue',
	done: 'green',
	failed: 'red',
};

const SOURCE_LABEL = {
	company_submission_proposal: 'Proposal',
	np: 'NP',
};

function formatDate(value) {
	if (!value) return '-';
	return new Date(value).toLocaleString();
}

function DigitizerMonitor() {
	const { t } = useTranslation();
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const isAdminUser = useMemo(() => isAdmin(user?.role), [user]);

	const [tabIndex, setTabIndex] = useState(0);
	const [page, setPage] = useState(0);
	const toast = useToast();
	const [detailItem, setDetailItem] = useState(null);
	const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
	const [addInputTokens, setAddInputTokens] = useState('');
	const [addOutputTokens, setAddOutputTokens] = useState('');

	const status = STATUS_TABS[tabIndex];

	const { data: statsData, loading: statsLoading, refetch: refetchStats } = useQuery(QUERY_STATS, {
		skip: !isAdminUser,
		pollInterval: 10000,
		fetchPolicy: 'network-only',
	});

	const {
		data: itemsData,
		loading: itemsLoading,
		error: itemsError,
		refetch: refetchItems,
	} = useQuery(QUERY_ITEMS, {
		skip: !isAdminUser,
		variables: { status, limit: PAGE_SIZE, offset: page * PAGE_SIZE },
		pollInterval: 10000,
		fetchPolicy: 'network-only',
	});

	const [retryItem, { loading: retryLoading }] = useMutation(RETRY_ITEM, {
		onCompleted: () => {
			refetchStats();
			refetchItems();
		},
		onError: (err) => {
			toast({ title: t('Failed to retry'), description: err?.message, status: 'error', duration: 4000 });
		},
	});

	const { data: budgetData, loading: budgetLoading, refetch: refetchBudget } = useQuery(QUERY_CLEANUP_BUDGET, {
		skip: !isAdminUser,
		pollInterval: 10000,
		fetchPolicy: 'network-only',
	});

	const { data: cleanupStatsData, refetch: refetchCleanupStats } = useQuery(QUERY_CLEANUP_STATS, {
		skip: !isAdminUser,
		pollInterval: 10000,
		fetchPolicy: 'network-only',
	});

	const [addTokens, { loading: addTokensLoading }] = useMutation(ADD_CLEANUP_TOKENS, {
		onCompleted: () => {
			toast({ title: t('Tokens added'), status: 'success', duration: 3000 });
			setAddInputTokens('');
			setAddOutputTokens('');
			refetchBudget();
			refetchCleanupStats();
		},
		onError: (err) => {
			toast({ title: t('Failed to add tokens'), description: err?.message, status: 'error', duration: 4000 });
		},
	});

	if (!isAdminUser) {
		return (
			<Container maxW="container.xl" mt="16px" mb="16px">
				<BreadcrumbComponent list={[{ name: 'Settings', path: '/settings' }, { name: 'Digitizer Monitor', path: '#' }]} />
				<Box mt="4" bg="white" borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="16px">
					<Text fontSize="13px" color="red.600">
						{t('You do not have access to this page')}
					</Text>
				</Box>
			</Container>
		);
	}

	const stats = statsData?.getDigitizationQueueStats;
	const items = itemsData?.getDigitizationQueueItems?.items || [];
	const totalRows = itemsData?.getDigitizationQueueItems?.totalRows || 0;
	const budget = budgetData?.getAiCleanupBudget;
	const cleanupStats = cleanupStatsData?.getDigitizationCleanupStats;
	const outOfBudget = budget && (budget.remainingInputTokens <= 0 || budget.remainingOutputTokens <= 0);

	return (
		<Container maxW="container.xl" mt="16px" mb="16px">
			<BreadcrumbComponent list={[{ name: 'Settings', path: '/settings' }, { name: 'Digitizer Monitor', path: '#' }]} />

			<Text fontSize="12px" color="gray.500" mt="4">
				{t('Newest proposals and NPs are picked up and OCR’d automatically, one at a time, newest first. Nothing to trigger manually.')}
			</Text>

			<Box mt="4" bg="white" borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="16px">
				<Text fontWeight="600" fontSize="14px" mb="1">
					{t('AI Cleanup')}
				</Text>
				<Text fontSize="12px" color="gray.500" mb="3">
					{t('Turning raw OCR text into cleaned text costs real AI tokens, so this runs only against a balance you add — it stops on its own when the balance runs out and picks back up, newest first, once you add more.')}
				</Text>

				{!budgetLoading && budget && (
					<SimpleGrid columns={[2, null, 4]} spacing="4" mb="4">
						<Box borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="12px">
							<Text fontSize="11px" color="gray.500" textTransform="uppercase">
								{t('Remaining input tokens')}
							</Text>
							<Text fontSize="20px" fontWeight="bold" color={outOfBudget ? 'red.600' : 'green.600'}>
								{budget.remainingInputTokens.toLocaleString()}
							</Text>
						</Box>
						<Box borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="12px">
							<Text fontSize="11px" color="gray.500" textTransform="uppercase">
								{t('Remaining output tokens')}
							</Text>
							<Text fontSize="20px" fontWeight="bold" color={outOfBudget ? 'red.600' : 'green.600'}>
								{budget.remainingOutputTokens.toLocaleString()}
							</Text>
						</Box>
						<Box borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="12px">
							<Text fontSize="11px" color="gray.500" textTransform="uppercase">
								{t('Awaiting cleanup')}
							</Text>
							<Text fontSize="20px" fontWeight="bold">
								{cleanupStats?.awaitingCleanup ?? 0}
							</Text>
						</Box>
						<Box borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="12px">
							<Text fontSize="11px" color="gray.500" textTransform="uppercase">
								{t('Cleaned')}
							</Text>
							<Text fontSize="20px" fontWeight="bold">
								{cleanupStats?.cleaned ?? 0}
							</Text>
						</Box>
					</SimpleGrid>
				)}

				{outOfBudget && (
					<Text fontSize="12px" color="orange.600" mb="3">
						{t('Out of budget — cleanup is paused until more tokens are added.')}
					</Text>
				)}

				<Flex gap="8px" align="flex-end" wrap="wrap">
					<Box>
						<Text fontSize="12px" color="gray.600" mb="4px">
							{t('Add input tokens')}
						</Text>
						<Input
							type="number"
							size="sm"
							min="0"
							value={addInputTokens}
							onChange={(e) => setAddInputTokens(e.target.value)}
							borderRadius={c.borderRadius}
							w="160px"
						/>
					</Box>
					<Box>
						<Text fontSize="12px" color="gray.600" mb="4px">
							{t('Add output tokens')}
						</Text>
						<Input
							type="number"
							size="sm"
							min="0"
							value={addOutputTokens}
							onChange={(e) => setAddOutputTokens(e.target.value)}
							borderRadius={c.borderRadius}
							w="160px"
						/>
					</Box>
					<Button
						size="sm"
						colorScheme="green"
						borderRadius={c.borderRadius}
						isLoading={addTokensLoading}
						isDisabled={!Number(addInputTokens) && !Number(addOutputTokens)}
						onClick={() =>
							addTokens({
								variables: { inputTokens: Number(addInputTokens) || 0, outputTokens: Number(addOutputTokens) || 0 },
							})
						}
					>
						{t('Add tokens')}
					</Button>
				</Flex>
			</Box>

			<Text fontWeight="600" fontSize="14px" mt="6" mb="1">
				{t('OCR Queue')}
			</Text>

			{!statsLoading && stats && (
				<SimpleGrid columns={[2, null, 4]} spacing="4" mt="1">
					{STATUS_TABS.map((key) => (
						<Box key={key} bg="white" borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="12px">
							<Text fontSize="11px" color="gray.500" textTransform="uppercase">
								{t(key)}
							</Text>
							<Text fontSize="20px" fontWeight="bold" color={`${STATUS_COLOR[key]}.600`}>
								{stats[key] ?? 0}
							</Text>
						</Box>
					))}
				</SimpleGrid>
			)}

			<Box mt="4" bg="white" borderRadius={c.borderRadius} border="1px solid #dbdbdb" p="16px" overflowX="auto">
				<Tabs
					index={tabIndex}
					onChange={(index) => {
						setTabIndex(index);
						setPage(0);
					}}
					size="sm"
				>
					<TabList>
						{STATUS_TABS.map((key) => (
							<Tab key={key}>{t(key)}</Tab>
						))}
					</TabList>
				</Tabs>

				{itemsLoading && (
					<Flex justify="center" py="24px">
						<Spinner />
					</Flex>
				)}

				{!itemsLoading && itemsError && (
					<Text fontSize="13px" color="red.600" mt="3">
						{t('Could not load the queue')}
					</Text>
				)}

				{!itemsLoading && !itemsError && items.length === 0 && (
					<Text fontSize="13px" color="gray.500" mt="3">
						{t('Nothing here')}
					</Text>
				)}

				{!itemsLoading && !itemsError && items.length > 0 && (
					<>
						<Table size="sm" mt="3">
							<Thead>
								<Tr>
									<Th>{t('Source')}</Th>
									<Th>{t('Source ID')}</Th>
									<Th>{t('Status')}</Th>
									<Th isNumeric>{t('Files')}</Th>
									<Th isNumeric>{t('Attempts')}</Th>
									<Th>{t('Error')}</Th>
									<Th>{t('Source date')}</Th>
									<Th>{t('Finished')}</Th>
									<Th></Th>
								</Tr>
							</Thead>
							<Tbody>
								{items.map((item) => (
									<Tr key={item.id} _hover={{ bg: 'gray.50' }}>
										<Td>{t(SOURCE_LABEL[item.sourceType] || item.sourceType)}</Td>
										<Td fontFamily="monospace" fontSize="11px">
											{item.sourceId}
										</Td>
										<Td>
											<Badge colorScheme={STATUS_COLOR[item.status] || 'gray'}>{t(item.status)}</Badge>
										</Td>
										<Td isNumeric>
											{item.fileCount != null ? `${item.fileCount - (item.failedFileCount || 0)}/${item.fileCount}` : '-'}
										</Td>
										<Td isNumeric>{item.attempts}</Td>
										<Td maxW="220px" fontSize="11px" color="red.600" isTruncated title={item.error || ''}>
											{item.error || '-'}
										</Td>
										<Td fontSize="11px">{formatDate(item.sourceCreatedAt)}</Td>
										<Td fontSize="11px">{formatDate(item.finishedAt)}</Td>
										<Td>
											<Flex gap="6px">
												{(item.status === 'done' || item.status === 'failed') && (
													<Button
														size="xs"
														onClick={() => {
															setDetailItem(item);
															onDetailOpen();
														}}
													>
														{t('View')}
													</Button>
												)}
												{item.status === 'failed' && (
													<Button size="xs" isLoading={retryLoading} onClick={() => retryItem({ variables: { id: item.id } })}>
														{t('Retry')}
													</Button>
												)}
											</Flex>
										</Td>
									</Tr>
								))}
							</Tbody>
						</Table>

						<Flex justify="space-between" align="center" mt="3">
							<Text fontSize="12px" color="gray.500">
								{t('Showing')} {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, totalRows)} {t('of')} {totalRows}
							</Text>
							<Flex gap="8px">
								<Button size="xs" isDisabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>
									{t('Previous')}
								</Button>
								<Button size="xs" isDisabled={(page + 1) * PAGE_SIZE >= totalRows} onClick={() => setPage((p) => p + 1)}>
									{t('Next')}
								</Button>
							</Flex>
						</Flex>
					</>
				)}
			</Box>

			<DigitizerDetailModal
				isOpen={isDetailOpen}
				onClose={onDetailClose}
				sourceType={detailItem?.sourceType}
				sourceId={detailItem?.sourceId}
			/>
		</Container>
	);
}

export default DigitizerMonitor;
